import { InvitationExpiredTime } from '../../constants/invitation';
import { RegionsCode } from '../../constants/region';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { UserId, Uuid } from '../Base';
import { RepositoryElement } from '../Repository';

export interface InvitationJson<T = number> {
  userId: Uuid;
  invitedBy: Uuid;
  invitedAt: T;
  inviterReward: Record<string, number>;

  userCode?: string;
}

export class Invitation
  extends RepositoryElement
  implements InvitationJson<Date>
{
  userId!: Uuid;
  invitedBy!: Uuid;
  invitedAt!: Date;
  inviterReward!: Record<string, number>;

  userCode?: string;

  constructor(invitation: InvitationJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(invitation));
  }

  id() {
    return this.userId;
  }

  toJSON(): InvitationJson {
    return deepTransformDate(this) as unknown as InvitationJson;
  }
}

export enum InvitationType {
  Referral = 'referral',
  SecondReferral = 'secondReferral',
}

export enum SystemInvitationType {
  ReservedName = 'reservedName',
  JoinClub = 'joinClub',
  OwnerShipTransfer = 'ownerShipTransfer',
}

export enum SystemInvitationStatus {
  NotReconfirmed = 'notReconfirmed',
  InvitationSent = 'invitationSent',
  InvitationSentAndSameName = 'invitationSentAndSameName',
  Claimed = 'claimed',
  PasswordSent = 'passwordSent',
  Accepted = 'accepted',
}

export enum SystemInvitationStatusName {
  NotReconfirmed = 'Not Reconfirmed',
  InvitationSent = 'Invitation Sent',
  Claimed = 'Claimed',
  PasswordSent = 'Password Sent',
  Expired = 'Expired',
  Accepted = 'Accepted',
}

export interface SystemInvitationJson<T = number> {
  systemInvitationId: Uuid;
  type: SystemInvitationType;
  createdBy: Uuid;
  createdAt: T;
  status: SystemInvitationStatus;
  reservedName?: string;
  email?: string;
  organization?: string;
  region?: RegionsCode;
  category?: string;
  followedUpBy?: UserId;
  classroomId?: Uuid;
  sender?: string;
  invitationSentAt?: T;
}

export class SystemInvitation
  extends RepositoryElement
  implements SystemInvitationJson<Date>
{
  systemInvitationId!: Uuid;
  type!: SystemInvitationType;
  createdBy!: Uuid;
  createdAt!: Date;
  status!: SystemInvitationStatus;
  reservedName?: string;
  email?: string;
  organization?: string;
  region?: RegionsCode;
  category?: string;
  followedUpBy?: UserId;
  classroomId?: Uuid;
  sender?: string;
  invitationSentAt?: Date;

  constructor(systemInvitation: SystemInvitationJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(systemInvitation));
  }

  id() {
    return this.systemInvitationId;
  }

  toJSON(): SystemInvitationJson {
    return deepTransformDate(this) as unknown as SystemInvitationJson;
  }

  get statusName() {
    switch (this.status) {
      case SystemInvitationStatus.NotReconfirmed:
        return SystemInvitationStatusName.NotReconfirmed;
      case SystemInvitationStatus.InvitationSent:
        if (this.invitationSentAt) {
          if (
            this.invitationSentAt.getTime() + InvitationExpiredTime >=
            Date.now()
          ) {
            return SystemInvitationStatusName.InvitationSent;
          }
        }
        return SystemInvitationStatusName.Expired;
      case SystemInvitationStatus.InvitationSentAndSameName:
        return SystemInvitationStatusName.InvitationSent;
      case SystemInvitationStatus.Claimed:
        return SystemInvitationStatusName.Claimed;
      case SystemInvitationStatus.PasswordSent:
        return SystemInvitationStatusName.PasswordSent;
      case SystemInvitationStatus.Accepted:
        return SystemInvitationStatusName.Accepted;
    }
  }
}

// user/invitation-links
export enum UserInvitationType {
  Referral = 'referral',
  NameCardSharing = 'nameCardSharing',
  NameCardTransfer = 'nameCardTransfer',
  LeaderboardTeamInvitation = 'leaderboardTeamInvitation',
  UserSharing = 'userSharing',
  PostSharing = 'postSharing',
  ClassroomSharing = 'classroomSharing',
  GameSetSharing = 'gameSetSharing',
  GameSharing = 'gameSharing',
  MultiplayerGameSharing = 'multiplayerGameSharing',
  SlideshowSharing = 'slideshowSharing',
  MultiplayerGameSessionInvitation = 'multiplayerGameSessionInvitation',
  FriendChallengeInvitation = 'friendChallengeInvitation',
}
// Define the content types
type ReferralContent = Record<string, never>;
type NameCardSharingContent = {
  nameCardId: Uuid;
};
type NameCardTransferContent = {
  nameCardId: Uuid;
};
type LeaderboardTeamInvitationContent = {
  classroomId: Uuid;
  leaderboardId: Uuid;
  teamId: Uuid;
};
type UserSharingContent = {
  userId: Uuid;
};
type PostSharingContent = {
  postId: Uuid;
};
type ClassroomSharingContent = {
  classroomId: Uuid;
};
type GameSetSharingContent = {
  classroomId: Uuid;
  gameSetId: Uuid;
};
type GameSharingContent = {
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  gameId: Uuid;
};
type MultiplayerGameSharingContent = {
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  multiplayerGameId: Uuid;
};
type SlideshowSharingContent = {
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  slideshowId: Uuid;
};
type MultiplayerGameSessionInvitationContent = {
  // gameSetId: Uuid;
  // gameCollectionId: Uuid;
  classroomId: Uuid;
  sessionId: Uuid;
  team: number;
  multiplayerGameId: Uuid;
  player: number;
};
type FriendChallengeInvitationContent = {
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  gameRecordId: Uuid;
  gameId: Uuid;
};

type InvitationTypeContentMap = {
  [UserInvitationType.Referral]: ReferralContent;
  [UserInvitationType.NameCardSharing]: NameCardSharingContent;
  [UserInvitationType.NameCardTransfer]: NameCardTransferContent;
  [UserInvitationType.LeaderboardTeamInvitation]: LeaderboardTeamInvitationContent;
  [UserInvitationType.UserSharing]: UserSharingContent;
  [UserInvitationType.PostSharing]: PostSharingContent;
  [UserInvitationType.ClassroomSharing]: ClassroomSharingContent;
  [UserInvitationType.GameSetSharing]: GameSetSharingContent;
  [UserInvitationType.GameSharing]: GameSharingContent;
  [UserInvitationType.MultiplayerGameSharing]: MultiplayerGameSharingContent;
  [UserInvitationType.SlideshowSharing]: SlideshowSharingContent;
  [UserInvitationType.MultiplayerGameSessionInvitation]: MultiplayerGameSessionInvitationContent;
  [UserInvitationType.FriendChallengeInvitation]: FriendChallengeInvitationContent;
};

export interface InvitationLinkJson<
  T = number,
  Type extends UserInvitationType = UserInvitationType,
> {
  invitationLinkId: Uuid;
  userId: Uuid;
  userCode: string;
  sendedAt: T;
  expiredAt: T;
  type: Type;
  content: InvitationTypeContentMap[Type];
  email?: string;
}

export class InvitationLink<
    Type extends UserInvitationType = UserInvitationType,
  >
  extends RepositoryElement
  implements InvitationLinkJson<Date, Type>
{
  invitationLinkId!: Uuid;
  userId!: Uuid;
  userCode!: string;
  sendedAt!: Date;
  expiredAt!: Date;
  type!: Type;
  content!: InvitationTypeContentMap[Type];
  email?: string;

  constructor(invitationLink: InvitationLinkJson<Date | number, Type>) {
    super();
    Object.assign(this, deepTransformTimestamp(invitationLink));
    // Assign the correct type and content based on the passed invitationLink
    this.type = invitationLink.type;
    this.content = invitationLink.content;
  }

  id() {
    return this.invitationLinkId;
  }

  toJSON(): InvitationLinkJson<Date, Type> {
    const transformedObject = deepTransformDate(this);
    return transformedObject as InvitationLinkJson<Date, Type>;
  }
}

export interface RedemptionCodeJson<T = number> {
  redemptionCode: string; // Trimmed and upper case, length: 4-32
  createdBy: Uuid; // User ID
  createdAt: T; // Creation date
  startedAt: T; // Start date
  expiredAt: T; // Expiry date
  name?: string | null; // Name, is optional
  quota: number | null; // Quota, can be null
  reward: Record<string, number>; // Reward object as an integer map
  redemptionCount: number; // Redemption count, integer >= 0
  webpageUi?: WebpageUI | null; // Optional UI type as enum or null
}
export class RedemptionCode
  extends RepositoryElement
  implements RedemptionCodeJson<Date>
{
  redemptionCode!: string;
  createdBy!: Uuid;
  createdAt!: Date;
  startedAt!: Date;
  expiredAt!: Date;
  name?: string | null;
  quota: number | null = null;
  reward!: Record<string, number>;
  redemptionCount!: number;
  webpageUi?: WebpageUI | null;

  constructor(redemptionCode: RedemptionCodeJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(redemptionCode));
  }

  id() {
    return this.redemptionCode;
  }

  toJSON(): RedemptionCodeJson<Date> {
    const transformedObject = deepTransformDate(this);
    return transformedObject as RedemptionCodeJson<Date>;
  }
}

export enum WebpageUI {
  BusinessEnglishESG = 'businessEnglish+ESG',
  InnovativeLearningMethod = 'innovativeLearningMethod',
  WorldCupBibleWeekly = 'worldCupBibleWeekly',
  BusinessLearningBible = 'business+learning+bible',
  WorldEducatorsAssociation = 'worldEducatorsAssociation',
}
